import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { Movie } from "@/types/movie";

export default function HeroCarousel({ items }: { items: Movie[] }) {
  const [index, setIndex] = useState(0);
  const count = items.length;

  const go = useCallback(
    (next: number) => {
      if (count === 0) return;
      setIndex(((next % count) + count) % count);
    },
    [count],
  );

  useEffect(() => {
    if (count <= 1) return;
    const timer = window.setInterval(() => {
      setIndex((prev) => (prev + 1) % count);
    }, 6000);
    return () => window.clearInterval(timer);
  }, [count]);

  if (count === 0) return null;

  const current = items[index];

  return (
    <section className="relative h-[460px] w-full overflow-hidden md:h-[600px]">
      {items.map((movie, i) => (
        <div
          key={movie.id}
          className={`absolute inset-0 transition-opacity duration-700 ${
            i === index ? "opacity-100" : "opacity-0"
          }`}
        >
          <img
            src={movie.backdrop || movie.cover}
            alt={`${movie.title} 焦点图`}
            title={`${movie.title} 在线观看`}
            className={`h-full w-full object-cover object-top ${
              i === index ? "animate-slow-zoom" : ""
            }`}
          />
        </div>
      ))}

      <div className="absolute inset-0 bg-gradient-to-r from-foreground-950/90 via-foreground-950/65 to-foreground-950/25" />
      <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/70 via-transparent to-foreground-950/30" />

      <div className="relative z-10 flex h-full items-center">
        <div className="w-full px-4 md:px-6">
          <div className="max-w-2xl">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-accent-500 px-3 py-1 text-xs text-background-50">
              <span className="flex h-3.5 w-3.5 items-center justify-center">
                <i className="ri-translate-2 text-sm" />
              </span>
              中文字幕 · 高清在线
            </span>
            <h1 className="mt-4 font-brand text-2xl text-background-50 md:text-3xl">
              日韩中文字幕在线观看
            </h1>
            <p className="mt-1 text-sm text-background-100/80">
              海量日韩影视剧在线播放 · 追剧更轻松
            </p>

            <h2 className="mt-5 text-2xl font-medium text-background-50 md:text-4xl">
              {current.title}
            </h2>
            <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-background-100/85">
              <span className="flex items-center gap-1">
                <span className="flex h-4 w-4 items-center justify-center text-secondary-400">
                  <i className="ri-star-fill text-sm" />
                </span>
                {current.rating.toFixed(1)}
              </span>
              <span>{current.year}</span>
              <span>{current.region}</span>
              <span>{current.type}</span>
              <span className="rounded bg-background-50/15 px-2 py-0.5">
                {current.genres.join(" / ")}
              </span>
            </div>
            <p className="mt-4 line-clamp-2 max-w-xl text-sm leading-relaxed text-background-100/85">
              {current.synopsis}
            </p>

            <div className="mt-6 flex flex-wrap gap-3">
              <Link
                to={`/detail/${current.id}`}
                className="flex items-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-5 py-2.5 text-sm text-background-50 transition-colors hover:bg-accent-600"
              >
                <span className="flex h-4 w-4 items-center justify-center">
                  <i className="ri-play-fill text-base" />
                </span>
                立即观看
              </Link>
              <Link
                to={`/category/${current.type === "电视剧" ? "drama" : "movie"}`}
                className="flex items-center gap-2 whitespace-nowrap rounded-md border border-background-50/40 px-5 py-2.5 text-sm text-background-50 transition-colors hover:bg-background-50/10"
              >
                查看更多同类
              </Link>
            </div>
          </div>
        </div>
      </div>

      {count > 1 && (
        <>
          <button
            type="button"
            aria-label="上一张"
            onClick={() => go(index - 1)}
            className="absolute left-3 top-1/2 z-10 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-background-50/15 text-background-50 backdrop-blur transition-colors hover:bg-background-50/30 md:left-6"
          >
            <i className="ri-arrow-left-s-line text-xl" />
          </button>
          <button
            type="button"
            aria-label="下一张"
            onClick={() => go(index + 1)}
            className="absolute right-3 top-1/2 z-10 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-background-50/15 text-background-50 backdrop-blur transition-colors hover:bg-background-50/30 md:right-6"
          >
            <i className="ri-arrow-right-s-line text-xl" />
          </button>
          <div className="absolute bottom-6 left-4 z-10 flex gap-2 md:left-6">
            {items.map((movie, i) => (
              <button
                key={movie.id}
                type="button"
                aria-label={`切换到 ${movie.title}`}
                onClick={() => go(i)}
                className={`h-1.5 rounded-full transition-all ${
                  i === index ? "w-8 bg-accent-500" : "w-4 bg-background-50/50"
                }`}
              />
            ))}
          </div>
        </>
      )}
    </section>
  );
}